import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import userApi from '../../api/userApi';
import actorApi from '../../api/actorApi';
import pmiApi from '../../api/pmiApi';
import { setCommunicationAccountLanguage } from '../../utils/accountLanguage';
import { renewTokenIfRightsChanged } from './tokenRights';
import { revokeVoicePushOnLogout } from '../../utils/voiceDevice';
import { authGeneration, sameAuthGeneration, endActorSupport, installSupportBoundary, isSupportToken } from '../../api/actorSupport';
import ActorSupportBanner from './ActorSupportBanner';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  useEffect(() => installSupportBoundary(), []);
  const [user, setUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState(() => {
    try {
      const stored = localStorage.getItem('userPermissions');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setCommunicationAccountLanguage(user);
  }, [user]);

  const updateAccountLanguage = async (preferredLanguage) => {
    const accountId = user?.id;
    if (!accountId) throw new Error('Bitte melden Sie sich an.');
    const result = await userApi.setPreferredLanguage(preferredLanguage);
    setUser((current) => current?.id === accountId ? { ...current, ...result } : current);
    return result;
  };

  const updateHeroPreferences = async (preferences) => {
    const accountId = user?.id;
    if (!accountId) throw new Error('Bitte melden Sie sich an.');
    const result = await userApi.setHeroPreferences(preferences);
    setUser((current) => current?.id === accountId ? { ...current, hero_enabled: result.enabled, hero_position: result.position } : current);
    return result;
  };

  // A password login can belong to a PMI customer as well as to an ERP user.
  // Resolve this server-side from the identity, then keep the marker only as
  // a routing convenience. PMI APIs still enforce the tenant and widget.
  const resolvePmiCustomerSession = async (identity = null) => {
    const identityRoles = Array.isArray(identity?.roles) ? identity.roles.map((role) => String(role).toLowerCase()) : [];
    if (identity?.is_admin === true || ['admin', 'administrator', 'superadmin'].some((role) => identityRoles.includes(role))) {
      localStorage.removeItem('pmiCustomerSession');
      localStorage.removeItem('loginChannel');
      return null;
    }
    try {
      const scope = await pmiApi.getPortalMe();
      if (scope?.customer_id && scope?.tenant_id) {
        localStorage.setItem('pmiCustomerSession', 'true');
        localStorage.setItem('loginChannel', 'customer');
        return scope;
      }
    } catch (_) {
      // Normal ERP users are expected to receive 403 from the customer scope.
    }
    localStorage.removeItem('pmiCustomerSession');
    return null;
  };

  const refreshToken = useCallback(async () => {
    const generation = authGeneration();
    const rt = localStorage.getItem('refreshToken');
    if (!rt) {
      localStorage.removeItem('token');
      setUser(null);
      return false;
    }

    try {
      const result = await Promise.race([
        userApi.refreshToken(rt),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Token refresh request timed out')), 10000))
      ]);
      if (!result || !result.access_token) throw new Error('Invalid token refresh response');
      if (!sameAuthGeneration(generation)) return false;

      localStorage.setItem('token', result.access_token);
      if (result.refresh_token) localStorage.setItem('refreshToken', result.refresh_token);

      try {
        const userData = await Promise.race([
          userApi.getCurrentUser(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('User data request timed out')), 8000))
        ]);
        setUser(userData);
        try {
          await Promise.race([
            (async () => {
              const permissions = await userApi.getUserPermissions();
              setUserPermissions(permissions);
            })(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Permissions request timed out')), 5000))
          ]);
        } catch (permissionsError) {
          console.warn('Could not fetch permissions after token refresh:', permissionsError);
        }
      } catch (userDataError) {
        try {
          const tokenParts = result.access_token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            setUser({
              id: payload.sub,
              email: localStorage.getItem('userEmail') || 'user@example.com',
              is_admin: payload.is_admin || false,
              roles: payload.roles || []
            });
          }
        } catch (tokenError) {
          console.error('Failed to extract user data from refreshed token:', tokenError);
        }
      }
      return true;
    } catch (err) {
      if (!sameAuthGeneration(generation)) return false;
      console.error('Token refresh failed:', err);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      setUser(null);
      return false;
    }
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      const generation = authGeneration();
      const token = localStorage.getItem('token');
      const passwordChangeRequired = localStorage.getItem('passwordChangeRequired');
      const passwordJustChanged = localStorage.getItem('passwordJustChanged');

      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3 && !isSupportToken(token)) {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          setLoading(false);
          return;
        }
      } catch (tokenError) {
        console.error('Error parsing token:', tokenError);
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        setLoading(false);
        return;
      }

      if (passwordChangeRequired === 'true') {
        setLoading(false);
        return;
      }

      if (passwordJustChanged === 'true') {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        setLoading(false);
        setUser(null);
        return;
      }

      try {
        const userData = await userApi.getCurrentUser();
        if (!sameAuthGeneration(generation)) return;

        if (userData && userData.email) {
          localStorage.setItem('userEmail', userData.email);
        }

        if (userData && userData.actor_id) {
          try {
            const actorData = await Promise.race([
              actorApi.getActor(userData.actor_id),
              new Promise((_, reject) => setTimeout(() => reject(new Error('Actor data request timed out')), 8000))
            ]);
            if (actorData && actorData.profile_picture_url) {
              userData.profile_picture_url = actorData.profile_picture_url;
            }
          } catch (actorError) {
            if (userData.email) {
              try {
                const searchResult = await actorApi.getActors(1, 10, { search: userData.email });
                const matchedActor = (searchResult?.actors || []).find(
                  a => a.email && a.email.toLowerCase() === userData.email.toLowerCase()
                );
                if (matchedActor) {
                  userData.actor_id = matchedActor.id;
                  if (matchedActor.profile_picture_url) {
                    userData.profile_picture_url = matchedActor.profile_picture_url;
                  }
                }
              } catch (searchError) {
                console.warn('Actor email search failed:', searchError);
              }
            }
          }
        }

        if (userData.permissions && Array.isArray(userData.permissions)) {
          localStorage.setItem('userPermissions', JSON.stringify(userData.permissions));
          setUserPermissions(userData.permissions);
          // Tags changed since login: renew the token before any module loads,
          // otherwise the menu shows a section whose API still says 403.
          await renewTokenIfRightsChanged(userData, userApi.refreshToken);
        }

        if (userData.is_admin !== undefined) {
          localStorage.setItem('isAdmin', JSON.stringify(userData.is_admin));
        }

        const pmiScope = await resolvePmiCustomerSession(userData);
        if (pmiScope) userData.pmi_scope = pmiScope;
        if (!sameAuthGeneration(generation)) return;

        setUser(userData);
        setError(null);
      } catch (err) {
        if (!sameAuthGeneration(generation)) return;
        console.error('Auth error:', err);
        setError(err.message);

        if (err.message.includes('inactive') || err.message.includes('password change')) {
          localStorage.setItem('passwordChangeRequired', 'true');
        } else if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
          try {
            const tokenParts = token.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              const minimalUser = {
                id: payload.sub,
                is_admin: payload.is_admin || false,
                roles: payload.roles || [],
                tags: payload.tags || [],
                actor_id: payload.actor_id || null,
                email: localStorage.getItem('userEmail') || 'user@example.com',
              };
              setUser(minimalUser);
              setError(null);

              if (payload.roles && Array.isArray(payload.roles)) {
                const basicPermissions = [];
                if (
                  payload.is_admin ||
                  payload.roles.includes('admin') ||
                  payload.roles.includes('Admin')
                ) {
                  basicPermissions.push(
                    'user_manage', 'actor_view', 'actor_edit',
                    'vehicle_view', 'vehicle_edit', 'repair_process_view',
                    'repair_process_edit', 'repair_process_create', 'tag_view', 'tag_manage'
                  );
                }
                if (payload.roles.includes('user') || payload.roles.includes('User')) {
                  basicPermissions.push('actor_view', 'vehicle_view', 'repair_process_view');
                }
                setUserPermissions(basicPermissions);
              }
              return;
            }
          } catch (tokenError) {
            console.error('Error extracting data from token:', tokenError);
          }
        } else if (err.message.includes('expired') || err.message.includes('invalid')) {
          await refreshToken();
        } else {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('passwordChangeRequired');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [refreshToken]);

  useEffect(() => {
    if (!user) return;
    const REFRESH_INTERVAL_MS = 20 * 60 * 1000;
    const doProactiveRefresh = async () => {
      const generation = authGeneration();
      const rt = localStorage.getItem('refreshToken');
      if (!rt) return;
      try {
        const result = await userApi.refreshToken(rt);
        if (!sameAuthGeneration(generation)) return;
        if (result?.access_token) {
          localStorage.setItem('token', result.access_token);
          if (result.refresh_token) localStorage.setItem('refreshToken', result.refresh_token);
        }
      } catch (err) {
        console.warn('[Session] Proactive refresh failed:', err.message);
      }
    };
    const intervalId = setInterval(doProactiveRefresh, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [user]);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const result = await Promise.race([
        userApi.login(email, password),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Login request timed out')), 15000))
      ]);

      if (result.refresh_token) localStorage.setItem('refreshToken', result.refresh_token);
      localStorage.removeItem('loginChannel');
      localStorage.removeItem('pmiCustomerSession');
      localStorage.removeItem('isAdmin');
      localStorage.removeItem('userPermissions');

      if (result.permissions) {
        localStorage.setItem('userPermissions', JSON.stringify(result.permissions));
        setUserPermissions(result.permissions);
      } else {
        setUserPermissions([]);
      }

      localStorage.setItem('isAdmin', JSON.stringify(result.is_admin === true));

      if (!result.passwordChangeRequired) {
        try {
          const userData = await Promise.race([
            userApi.getCurrentUser(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('User data request timed out')), 10000))
          ]);
          userData.permissions = result.permissions || [];
          userData.is_admin = result.is_admin === true || userData.is_admin === true;
          const pmiScope = await resolvePmiCustomerSession(userData);
          if (pmiScope) userData.pmi_scope = pmiScope;
          setUser(userData);
        } catch (userDataError) {
          if (result.access_token) {
            try {
              const tokenParts = result.access_token.split('.');
              if (tokenParts.length === 3) {
                const payload = JSON.parse(atob(tokenParts[1]));
                setUser({
                  id: payload.sub,
                  email,
                  is_admin: result.is_admin === true || payload.is_admin === true,
                  roles: payload.roles || [],
                  permissions: result.permissions || []
                });
              }
            } catch (tokenError) {
              console.error('Failed to extract user data from token:', tokenError);
            }
          }
        }
      }

      setError(null);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const customerLogin = async (identifier, code) => {
    setLoading(true);
    try {
      const result = await userApi.verifyCustomerOtp(identifier, code);
      if (result.access_token) localStorage.setItem('token', result.access_token);
      if (result.refresh_token) localStorage.setItem('refreshToken', result.refresh_token);
      localStorage.setItem('loginChannel', 'customer');
      localStorage.removeItem('isAdmin');
      localStorage.removeItem('userPermissions');

      const permissions = result.permissions || [];
      localStorage.setItem('userPermissions', JSON.stringify(permissions));
      setUserPermissions(permissions);
      localStorage.setItem('isAdmin', 'false');

      const customerUser = {
        id: result.user_id,
        email: identifier.includes('@') ? identifier : `phone-${identifier.replace(/\D/g, '')}@no-email.escooterlab.ch`,
        is_admin: false,
        roles: ['Customer'],
        permissions,
        actor_id: result.actor_id || null
      };

      try {
        const userData = await userApi.getCurrentUser();
        if (userData) {
          userData.permissions = permissions;
          userData.is_admin = false;
          setUser(userData);
        } else {
          setUser(customerUser);
        }
      } catch (userDataError) {
        setUser(customerUser);
      }

      setError(null);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    if (isSupportToken(localStorage.getItem('token'))) {
      await endActorSupport(false);
      return;
    }
    setLoading(true);
    const purgeUserData = () => {
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('isAdmin');
        localStorage.removeItem('userPermissions');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('loginChannel');
        localStorage.removeItem('pmiCustomerSession');
        localStorage.removeItem('passwordChangeRequired');
        localStorage.removeItem('sidebarVisible');
        const toRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('repair-process:recently-visited')) toRemove.push(key);
        }
        toRemove.forEach(key => localStorage.removeItem(key));
      } catch (_) {
        // localStorage unavailable
      }
    };

    try {
      await Promise.all([userApi.logout(), revokeVoicePushOnLogout(user?.id).catch(() => {})]);
    } catch (err) {
      setError(err.message);
    } finally {
      purgeUserData();
      setUser(null);
      setUserPermissions([]);
      setError(null);
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      setLoading(true);
      const userData = await userApi.getCurrentUser();
      if (userData && userData.actor_id) {
        try {
          const actorData = await Promise.race([
            actorApi.getActor(userData.actor_id),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Actor data request timed out')), 8000))
          ]);
          if (actorData && actorData.profile_picture_url) {
            userData.profile_picture_url = actorData.profile_picture_url;
          }
        } catch (actorError) {
          if (userData.email) {
            try {
              const searchResult = await actorApi.getActors(1, 10, { search: userData.email });
              const matchedActor = (searchResult?.actors || []).find(
                a => a.email && a.email.toLowerCase() === userData.email.toLowerCase()
              );
              if (matchedActor) {
                userData.actor_id = matchedActor.id;
                if (matchedActor.profile_picture_url) {
                  userData.profile_picture_url = matchedActor.profile_picture_url;
                }
              }
            } catch (searchError) {
              console.warn('Actor email search failed on refresh:', searchError);
            }
          }
        }
      }
      setUser(userData);
      if (Array.isArray(userData?.permissions)) {
        setUserPermissions(userData.permissions);
        localStorage.setItem('userPermissions', JSON.stringify(userData.permissions));
      }
      setError(null);
    } catch (err) {
      console.error('Error refreshing user:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Frontend authorization is display/navigation convenience only. It mirrors
  // the persisted authorization returned by the backend and never derives
  // privilege from an email address.
  const hasRole = (roleName) => {
    if (!user) return false;
    if (user.is_admin === true) return true;
    return Array.isArray(user.roles) && user.roles.includes(roleName);
  };

  const hasAnyRole = (roleNames) => {
    if (!user) return false;
    if (user.is_admin === true) return true;
    if (!Array.isArray(user.roles) || !Array.isArray(roleNames)) return false;
    return roleNames.some(role => user.roles.includes(role));
  };

  const hasAllRoles = (roleNames) => {
    if (!user) return false;
    if (user.is_admin === true) return true;
    if (!Array.isArray(user.roles) || !Array.isArray(roleNames)) return false;
    return roleNames.every(role => user.roles.includes(role));
  };

  const currentPermissions = () => {
    let perms = userPermissions;
    if (!Array.isArray(perms) || perms.length === 0) {
      try {
        perms = JSON.parse(localStorage.getItem('userPermissions') || '[]');
      } catch {
        perms = [];
      }
    }
    return Array.isArray(perms) ? perms : [];
  };

  const hasPermission = (permissionKey) => {
    if (user?.is_admin === true) return true;
    const perms = currentPermissions();
    return perms.includes(permissionKey) || perms.includes('admin_access');
  };

  const hasAnyPermission = (permissionKeys) => {
    if (user?.is_admin === true) return true;
    if (!Array.isArray(permissionKeys)) return false;
    return permissionKeys.some(perm => hasPermission(perm));
  };

  const getLocationFilters = () => {
    if (user?.is_admin === true) return null;
    const perms = currentPermissions();
    if (perms.includes('admin_access')) return null;
    const cities = perms
      .filter(p => typeof p === 'string' && p.startsWith('location_filter:'))
      .map(p => p.replace('location_filter:', '').trim())
      .filter(Boolean);
    return cities.length > 0 ? cities.join(',') : null;
  };

  const getCategoryFilters = () => {
    if (user?.is_admin === true) return null;
    const perms = currentPermissions();
    if (perms.includes('admin_access')) return null;
    const categoryIds = perms
      .filter(p => typeof p === 'string' && p.startsWith('vehicle_category:'))
      .map(p => p.replace('vehicle_category:', '').trim())
      .filter(Boolean);
    return categoryIds.length > 0 ? categoryIds : null;
  };

  const authLoading = loading;
  const isAdmin = user?.is_admin === true || currentPermissions().includes('admin_access');

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!user,
        user,
        userPermissions,
        loading,
        error,
        login,
        customerLogin,
        logout,
        refreshUser,
        updateAccountLanguage,
        updateHeroPreferences,
        hasRole,
        hasAnyRole,
        hasAllRoles,
        hasPermission,
        hasAnyPermission,
        getLocationFilters,
        getCategoryFilters,
        isAdmin,
        // Compatibility fields retained as safe constants for existing consumers.
        // There is no frontend super-admin concept anymore.
        isSuperAdmin: false,
        superRole: null,
        refreshToken,
        authLoading
      }}
    >
      <ActorSupportBanner />
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
